import * as ToastPrimitive from '@radix-ui/react-toast'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { cn } from '../cn'

type ToastTone = 'info' | 'success' | 'warning' | 'danger'
interface ToastItem {
  id: number
  title: string
  description?: string
  tone: ToastTone
}

const ICONS: Record<ToastTone, React.ComponentType<{ className?: string }>> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
}

const TONE_CLASS: Record<ToastTone, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
}

const ToastContext = createContext<((toast: Omit<ToastItem, 'id'>) => void) | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const push = useCallback((toast: Omit<ToastItem, 'id'>) => {
    setToasts((current) => [...current, { ...toast, id: Date.now() + Math.random() }])
  }, [])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const value = useMemo(() => push, [push])

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}
        {toasts.map((toast) => {
          const Icon = ICONS[toast.tone]
          return (
            <ToastPrimitive.Root
              key={toast.id}
              onOpenChange={(open) => !open && dismiss(toast.id)}
              className={cn(
                'flex items-start gap-2.5 rounded-[var(--radius-panel)] border border-rule bg-surface p-3 shadow-(--shadow-popover)',
                'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-4',
              )}
            >
              <Icon className={cn('mt-0.5 size-4 shrink-0', TONE_CLASS[toast.tone])} />
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title className="text-base font-medium text-ink">{toast.title}</ToastPrimitive.Title>
                {toast.description ? (
                  <ToastPrimitive.Description className="mt-0.5 text-xs text-ink-muted">
                    {toast.description}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
              <ToastPrimitive.Close aria-label="Schließen" className="text-ink-faint hover:text-ink">
                <X className="size-3.5" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          )
        })}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[60] flex w-80 flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const push = useContext(ToastContext)
  if (!push) throw new Error('useToast muss innerhalb von ToastProvider verwendet werden')
  return push
}
